import { Injectable } from '@angular/core';
import { Observable, merge, Subject, EMPTY, BehaviorSubject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { AuthService } from './auth.service';
import { switchMap, map, tap, shareReplay } from 'rxjs/operators';
import { Channel } from '../models/channel';
import { Webhook } from '../models/webhook';

type IntegrationsResponse = {
  integrations: Channel[]
}

type WebhooksResponse = {
  webhooks: Webhook[]
}

type WebhookResponse = {
  webhook: Webhook
}

type RetentionResponse = {
  retention: number
}

@Injectable({
  providedIn: 'root'
})
export class SmoochService {

  private _reloadIntegrations$: Subject<Date> = new Subject<Date>();
  private _reloadWebhooks$: Subject<Date> = new Subject<Date>();
  private _reloadRetention$: Subject<Date> = new Subject<Date>();

  integrations$: Observable<Channel[]> = new Observable<Channel[]>();
  webhooks$: Observable<Webhook[]> = new Observable<Webhook[]>();
  retention$: Observable<number> = new Observable<number>();

 

  constructor(
    private _http: HttpClient,
    private _authService: AuthService
  ) {
    this.integrations$ = merge(
      this._authService.credential$,
      this._reloadIntegrations$
    ).pipe(
      switchMap(() => this.getIntegrations()),
      shareReplay(1)
    );

    this.webhooks$ = merge(
      this._authService.credential$,
      this._reloadWebhooks$
    ).pipe(
      switchMap(() => this.getWebhooks()),
      shareReplay(1)
    );

  }

  private getIntegrations(): Observable<Channel[]> {
    const credential = this._authService.credential$.getValue();
    const body = credential;
    return !credential ? EMPTY :

      this._http.post<any>(`/api/app/getIntegrations`,
        body,
        {
          headers: {
            'authorization': `basic ${btoa(`${credential.keyId}:${credential.secret}`)}`,
            'Content-Type': 'application/json'
          }
        })

        .pipe(
      map<IntegrationsResponse, Channel[]>(response => response.integrations)
    );
  }


  
  reloadIntegrations(): void {
    this._reloadIntegrations$.next(new Date());
  }

  reloadWebhooks(): void {
    this._reloadWebhooks$.next(new Date());
  }

  createIntegration(channel: string, data: any) {
    const credential = this._authService.credential$.getValue();
    const body = data;

    body.credential = credential;
    body.channel = channel;
    return this._http.post(`/api/app/createIntegration`,
      body,
      {
        headers: {
          'authorization': `basic ${btoa(`${credential.keyId}:${credential.secret}`)}`,
          'Content-Type': 'application/json'
        }
      }).pipe(
      tap(() => this.reloadIntegrations(), () => this.reloadIntegrations())
    );
  }

  deleteIntegration(integrationId: string) {
    const credential = this._authService.credential$.getValue();

    const body = {
      credential: credential,
      integrationId: integrationId
    };

    return this._http.post(`/api/app/deleteIntegration`,
      body,
      {
        headers: {
          'authorization': `basic ${btoa(`${credential.keyId}:${credential.secret}`)}`,
          'Content-Type': 'application/json'
        }
      }).pipe(
      tap(() => this.reloadIntegrations(), () => this.reloadIntegrations())
    );
  }

  private getWebhooks(): Observable<Webhook[]> {
    const credential = this._authService.credential$.getValue();
    const body = credential;

    return !credential ? EMPTY :

      this._http.post(`/api/app/getWebhooks`,
        body,
        {
          headers: {
            'authorization': `basic ${btoa(`${credential.keyId}:${credential.secret}`)}`,
            'Content-Type': 'application/json'
          }
        })
        .pipe(
      map<WebhooksResponse, Webhook[]>(response => response.webhooks)
    );
  }

  createWebhook(target: string): Observable<Webhook> {
    const credential = this._authService.credential$.getValue();
 
    const body = {
      credential: credential,
      target: target
    };

    return this._http.post(`/api/app/createWebhook`, 
      body,
      {
        headers: {
          'authorization': `basic ${btoa(`${credential.keyId}:${credential.secret}`)}`,
          'Content-Type': 'application/json'
        }
      }).pipe(
      tap(() => this.reloadWebhooks(), () => this.reloadWebhooks()),
      map<WebhookResponse, Webhook>(response => response.webhook)
    );
  }

  updateWebhook(webhook: Webhook): Observable<Webhook> {
    const credential = this._authService.credential$.getValue();
    const body = {
      credential: credential,
      webhook: webhook
    };

    return this._http.post(`/api/app/updateWebhook`,
      body,
      {
        headers: {
          'authorization': `basic ${btoa(`${credential.keyId}:${credential.secret}`)}`,
          'Content-Type': 'application/json'
        }
      })
      .pipe(
      tap(() => this.reloadWebhooks(), () => this.reloadWebhooks()),
      map<WebhookResponse, Webhook>(response => response.webhook)
    );
  }

  deleteWebhook(webhookId: string) {
    const credential = this._authService.credential$.getValue();
    const body = {
      credential: credential,
      webhookId: webhookId
    };

    return this._http.post(`/api/app/deleteWebhook`,
      body,
      {
        headers: {
          'authorization': `basic ${btoa(`${credential.keyId}:${credential.secret}`)}`,
          'Content-Type': 'application/json'
        }
      })
      .pipe(
      tap(() => this.reloadWebhooks(), () => this.reloadWebhooks())
    );
  }

  getApp(): Promise<any> {
    return this._http.get<any>('/api/app/getApps').toPromise();
  }


  updateRetention(retentionHours: number): Observable<number> {
    const credential = this._authService.credential$.getValue();
    const body = {
      credential: credential,
      retention: retentionHours*3600
    };

    return this._http.post(`/api/app/updateRetention`,
      body,
      {
        headers: {
          'authorization': `basic ${btoa(`${credential.keyId}:${credential.secret}`)}`,
          'Content-Type': 'application/json'
        }
      })
      .pipe(
        map<RetentionResponse, number>(response => {
          if (response.retention == 0) {
            return 0;
          }
          else {
            return response.retention / 3600;
          }
        })
      );
  }

  reloadRetention(): void {
    this._reloadRetention$.next(new Date());
  }


  getRetention(): Promise<number> {
  
    const credential = this._authService.credential$.getValue();
    const body = credential;

    return !credential ? EMPTY.toPromise() :
    this._http.post(

      `/api/app/getRetention`,
      body,
      {
        headers: {
          'authorization': `basic ${btoa(`${credential.keyId}:${credential.secret}`)}`,
          'Content-Type': 'application/json'
        }
      }).pipe(
        map<RetentionResponse, number>(response => {
          if (response.retention == 0) {
            return 0;
          }
          else {
            return response.retention / 3600;
          }
        })
      ).toPromise();
  }

}
